/* import __COLOCATED_TEMPLATE__ from './first-time-ux-tooltip.hbs.js'; */
var _dec, _dec2, _class, _descriptor, _descriptor2;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { get as getFromLocalStorage, set as setToLocalStorage } from '@square/dashboard-utilities/utils/persistable';
import { hasConsent, OneTrustConsent } from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';
const LOCAL_STORAGE_PREFIX = 'dashboard.first-time-ux';
/**
 * A `market-tooltip` wrapper to display First-Time User Experience:
 * Checks local storage for the passed-in `token` to determine whether to
 * show the tooltip. The tooltip content is shown until manually dismissed.
 * Nothing is shown if localStorage cannot be accessed to avoid unskippable
 * tooltips.
 *
 * Example usage:
 * <First-Time-Ux::First-Time-Ux-Tooltip
 *   @token={{"itemLibrary.selectAllHint"}}>
 *   <:trigger>
 *     <SelectAllCheckbox ... />
 *   </:trigger>
 *   <:content>
 *     <SelectAllHint ... />
 *   </:content>
 * </First-Time-Ux::First-Time-Ux-Tooltip>
 *
 * @class FirstTimeUXTooltip
 * @extends {Component}
 * @param {MarketTooltip} [multiple] Optional MarketTooltip props to be passed down
 *                        to the market-tooltip component. `interaction` and `disabled`
 *                        recommended to leave at default for FTUX.
 * @param {String} [token] Token used to determine whether to show FTUX.
 * @param {String} [slot] Optional slot to pass to the market-tooltip component.
 * @param {Boolean} [hideDismiss] Optional flag to prevent the "Dismiss" link in the content
 *                        from rendering. Default false
 * @param {Number} [activationDelayMs] Optional to control the wait between first render and
 *                        opening the tooltip. May need to be increased depending on the nature
 *                        of the trigger. Default 100
 * @slot trigger - Required component to render the tooltip around.
 * @slot content - Required component to render within the tooltip.
 */
let FirstTimeUXTooltip = (_dec = computed('args.disabled', 'hasCookieConsent', 'previouslyDismissed'), _dec2 = computed('token'), (_class = class FirstTimeUXTooltip extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "hasCookieConsent", _descriptor, this);
    _initializerDefineProperty(this, "previouslyDismissed", _descriptor2, this);
  }
  // Props (passed in or default)
  get token() {
    const argToken = this.args.token ?? '';
    return `${LOCAL_STORAGE_PREFIX}.${argToken}`;
  }
  get tooltipDisabled() {
    return this.args.disabled || this.previouslyDismissed || !this.hasCookieConsent;
  }
  get tooltipInteraction() {
    return this.args.interaction || 'persistent';
  }
  get tooltipPopoverDistance() {
    return this.args.popoverDistance ?? 8;
  }
  get tooltipPopoverPlacement() {
    return this.args.popoverPlacement || 'auto';
  }
  get tooltipPopoverSkidding() {
    return this.args.popoverSkidding;
  }
  get tooltipPopoverStrategy() {
    return this.args.popoverStrategy || 'absolute';
  }
  get hideDismiss() {
    return this.args.hideDismiss ?? false;
  }
  get activationDelayMs() {
    return this.args.activationDelayMs ?? 100;
  }
  get triggerElementId() {
    // Replace dots in the token as they interfere with query selection due to ambiguity with CSS class names
    // We can replace the split/join with replaceAll once we compile to es2021+
    return `${this.token.split('.').join('-')}-tooltip-id`;
  }
  onTooltipDismiss() {
    this.previouslyDismissed = true;
    setToLocalStorage(this.token, true, OneTrustConsent.FUNCTIONAL);
  }
  dismissTooltip() {
    this.onTooltipDismiss();
    this.toggleVisible();
  }
  forceDisplayTooltip() {
    // delay buffer to ensure any animations can complete
    setTimeout(() => {
      this.toggleVisible();
    }, this.activationDelayMs);
  }
  toggleVisible() {
    const trigger = this.getTriggerElement();
    trigger?.click();
  }
  getTriggerElement() {
    const selector = `#${this.triggerElementId}`;
    return document.querySelector(selector);
  }
}, (_applyDecoratedDescriptor(_class.prototype, "tooltipDisabled", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "tooltipDisabled"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "triggerElementId", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "triggerElementId"), _class.prototype), _descriptor = _applyDecoratedDescriptor(_class.prototype, "hasCookieConsent", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return hasConsent(OneTrustConsent.FUNCTIONAL);
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "previouslyDismissed", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return Boolean(getFromLocalStorage(this.token));
  }
}), _applyDecoratedDescriptor(_class.prototype, "onTooltipDismiss", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onTooltipDismiss"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dismissTooltip", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dismissTooltip"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "forceDisplayTooltip", [action], Object.getOwnPropertyDescriptor(_class.prototype, "forceDisplayTooltip"), _class.prototype)), _class));
export { FirstTimeUXTooltip as default };